import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { queryClient, TypedMutationConfig } from '@/lib/react-query';
import { ApiResponse, AxiosErrorCustom, axiosInstance } from '@/lib/axios';
import { TGetListAppStoreChannelResponse } from './get-list-app-store-channel.api';
import { GET_LIST_APP_STORE_CHANNEL_QUERY_KEY } from './get-list-app-store-channel.api';
import { TChannel } from '@/features/channel-integration/_shared/types/channel.type';

/**
 * Delete Integration Google Review Input Schema
 */
export const deleteAppStoreChannelValidationSchema = z.object({
  organizationChannelId: z.string({ required_error: 'Organization channel id is required' }),
});
export type TDeleteAppStoreChannelValidation = z.infer<typeof deleteAppStoreChannelValidationSchema>;
export type TDeleteAppStoreChannelResponse = ApiResponse<TChannel>;

export const deleteAppStoreChannel = async (
  payload: TDeleteAppStoreChannelValidation
): Promise<TDeleteAppStoreChannelResponse> => {
  const url = `/integration/appstore/org-channel/${payload.organizationChannelId}`;

  return axiosInstance.delete(url);
};

/**
 * Use Delete Google Review Integration React-Query Hook
 */
type UseDeleteAppStoreChannelOptions = {
  mutationConfig?: TypedMutationConfig<typeof deleteAppStoreChannel, AxiosErrorCustom>;
};

export const useDeleteAppStoreChannel = ({ mutationConfig }: UseDeleteAppStoreChannelOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: deleteAppStoreChannel,
    ...restConfig,
  });
};

/**
 * Use Delete Google Review Integration Optimistically React-Query Hook
 */
type UseDeleteAppStoreChannelOptimisticallyOptions = {
  mutationConfig?: TypedMutationConfig<typeof deleteAppStoreChannel, AxiosErrorCustom>;
};

type TDeleteAppStoreChannelOptimisticallyContext = {
  previousData: TGetListAppStoreChannelResponse | undefined;
};

export const useDeleteAppStoreChannelOptimistically = ({
  mutationConfig,
}: UseDeleteAppStoreChannelOptimisticallyOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: deleteAppStoreChannel,
    onMutate: async (data: TDeleteAppStoreChannelValidation) => {
      await queryClient.cancelQueries({ queryKey: [GET_LIST_APP_STORE_CHANNEL_QUERY_KEY] });

      const previousData = queryClient.getQueryData<TGetListAppStoreChannelResponse>([
        GET_LIST_APP_STORE_CHANNEL_QUERY_KEY,
      ]);

      queryClient.setQueriesData<TGetListAppStoreChannelResponse>(
        { queryKey: [GET_LIST_APP_STORE_CHANNEL_QUERY_KEY] },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            payload: {
              ...oldData.payload,
              rows: oldData.payload.rows.filter(
                (channel) => channel.organizationChannelId !== data.organizationChannelId
              ),
            },
          };
        }
      );

      return { previousData } as TDeleteAppStoreChannelOptimisticallyContext;
    },

    onError: (_err, _data, context) => {
      if ((context as TDeleteAppStoreChannelOptimisticallyContext)?.previousData !== undefined) {
        queryClient.setQueryData(
          [GET_LIST_APP_STORE_CHANNEL_QUERY_KEY],
          (context as TDeleteAppStoreChannelOptimisticallyContext).previousData
        );
      }
    },

    ...restConfig,
  });
};
